import React, { Component } from 'react';
import { connect } from 'react-redux';

import { startCase } from '../../../../common/utils';
import { searchOrigins } from '../../../Navigation/constants';
import { SearchFeatures } from '../../SearchTypes';
import {
  setHideSubMenu,
  updateSearchOptionsAndRefresh,
} from '../../actions/SearchActions';
import AppliedFilter from '../../components/appliedFilters/AppliedFilter';
import Color from '../../components/appliedFilters/Color';
import ContentType from '../../components/appliedFilters/ContentType';
import ResultCount from '../../components/appliedFilters/ResultCount';
import {
  categories as categoryOptions,
  orientations as orientationOptions,
} from '../../entities/ImageSearchFilterOptions';
import SearchFilterOptions, {
  ALL_IMAGES_CONTENT_TYPE,
} from '../../entities/SearchFilterOptions';
import { UsageRights } from '../../entities/UsageRights';
import {
  selectPagination,
  selectSearchFeatures,
  selectSearchFilterOptions,
  selectSearchIsLoading,
  selectShouldUpdateSearchFilters,
} from '../../selectors/searchSelectors';
import { ImageSelectedSearchFilterOptions } from '../MenuContainerInterfaces';
import { getOptionLabel } from './AppliedFiltersUtils';

import './AppliedFilters.less';

interface Props {
  selectedSearchFilterOptions: ImageSelectedSearchFilterOptions;
  numberOfResults: number;
  isLoading: boolean;
  setHideSubMenu: typeof setHideSubMenu;
  updateSearchOptionsAndRefresh: typeof updateSearchOptionsAndRefresh;
  features: SearchFeatures;
}

class GraphicAppliedFilters extends Component<Props> {
  shouldComponentUpdate(nextProps) {
    return nextProps.shouldUpdateSearchFilters !== false;
  }

  render() {
    if (this.props.features?.useRemainingSearchUI) {
      return this.renderNew();
    }
    const className = 'applied-filters md:flex flex flex-nowrap md:flex-wrap';

    return (
      <div className={className}>
        {this.renderSearchTerm()}
        {this.renderContentType()}
        {this.renderOrientation()}
        {this.renderColor()}
        {this.renderMediaDetails()}
        {this.renderUsageRights()}
        {this.renderCategory()}
        {this.renderHasTransparency()}
        {this.renderResultCount()}
      </div>
    );
  }

  hasAppliedFilters = () => {
    const {
      searchTerm,
      contentType,
      orientation,
      color,
      talentReleased,
      propertyReleased,
      usageRights,
      categories,
      hasTransparency,
    } = this.props.selectedSearchFilterOptions;
    return (
      searchTerm ||
      contentType !== 'all-images' ||
      orientation !== 'all' ||
      color ||
      talentReleased ||
      propertyReleased ||
      (usageRights !== UsageRights.All && usageRights) ||
      categories ||
      hasTransparency
    );
  };

  renderClearFiltersButton() {
    return (
      <button
        className="clear-all-btn text-xs underline border-none bg-none text-black cursor-pointer mr-2.5 w-fit pt-0"
        onClick={this.clearAllFilters}
      >
        Clear All
      </button>
    );
  }

  clearAllFilters = () => {
    const resetFilters = {
      searchTerm: '',
      contentType: ALL_IMAGES_CONTENT_TYPE,
      orientation: 'all',
      color: '',
      talentReleased: false,
      propertyReleased: false,
      usageRights: UsageRights.All,
      categories: '',
      hasTransparency: false,
    };

    const newSearchOptions =
      this.props.selectedSearchFilterOptions.update(resetFilters);
    this.props.updateSearchOptionsAndRefresh(newSearchOptions);

    this.props.setHideSubMenu();
  };
  renderNew() {
    const filtersApplied = this.hasAppliedFilters();
    const className = 'applied-filters md:flex flex flex-nowrap md:flex-wrap';

    return (
      <div
        className={`flex w-full ${
          filtersApplied ? 'justify-between' : 'justify-end'
        }`}
      >
        {filtersApplied && (
          <div className={className}>
            {this.renderSearchTerm()}
            {this.renderContentType()}
            {this.renderOrientation()}
            {this.renderColor()}
            {this.renderMediaDetails()}
            {this.renderUsageRights()}
            {this.renderCategory()}
            {this.renderHasTransparency()}
            {this.renderClearFiltersButton()}
          </div>
        )}
        {this.renderResultCount('mb-0 mt-1.5 pb-2.5')}
      </div>
    );
  }

  renderSearchTerm() {
    const { searchTerm } = this.props.selectedSearchFilterOptions;

    if (searchTerm) {
      const label = `"${searchTerm}"`;
      return (
        <AppliedFilter
          label={label}
          onClear={() => {
            this.updateFilters({ searchTerm: '' });
          }}
        />
      );
    } else {
      return null;
    }
  }

  renderContentType() {
    const { contentType } = this.props.selectedSearchFilterOptions;

    if (contentType === ALL_IMAGES_CONTENT_TYPE) {
      return null;
    } else {
      return (
        <ContentType
          appliedContentType={contentType}
          allContentTypes={SearchFilterOptions.getContentTypes()}
          onClear={() =>
            this.updateFilters({
              contentType: ALL_IMAGES_CONTENT_TYPE,
            })
          }
        />
      );
    }
  }

  renderOrientation() {
    const { orientation } = this.props.selectedSearchFilterOptions;

    if (orientation === 'all') {
      return null;
    } else {
      const label = getOptionLabel(orientation, orientationOptions);
      return (
        <AppliedFilter
          label={label}
          onClear={() => this.updateFilters({ orientation: 'all' })}
        />
      );
    }
  }

  renderColor() {
    const { color } = this.props.selectedSearchFilterOptions;

    if (!color) {
      return null;
    } else {
      const swatchStyle = {
        width: '16px',
        height: '16px',
        backgroundColor: `#${color}`,
        borderRadius: '2px',
        marginRight: '4px',
        display: 'inline-block',
        verticalAlign: 'middle',
      };

      return (
        <Color
          color={color}
          swatchStyle={swatchStyle}
          onClear={() => {
            this.updateFilters({ color: '' });
          }}
        />
      );
    }
  }

  renderMediaDetails() {
    const { talentReleased, propertyReleased } =
      this.props.selectedSearchFilterOptions;

    return (
      <React.Fragment>
        {talentReleased ? (
          <AppliedFilter
            label={'Model Released'}
            onClear={() => {
              this.updateFilters({ talentReleased: false });
            }}
          />
        ) : null}
        {propertyReleased ? (
          <AppliedFilter
            label={'Property Released'}
            onClear={() => {
              this.updateFilters({ propertyReleased: false });
            }}
          />
        ) : null}
      </React.Fragment>
    );
  }

  renderUsageRights() {
    const { usageRights } = this.props.selectedSearchFilterOptions;

    if (!usageRights || usageRights === UsageRights.All) {
      return null;
    }

    const label = startCase(usageRights);
    const onClear = () => this.updateFilters({ usageRights: UsageRights.All });

    return <AppliedFilter label={label} onClear={onClear} />;
  }

  renderCategory() {
    const { contentType, categories } = this.props.selectedSearchFilterOptions;

    if (!categories || contentType === ALL_IMAGES_CONTENT_TYPE) {
      return null;
    } else {
      const label = getOptionLabel(categories, categoryOptions);
      return (
        <AppliedFilter
          label={label}
          onClear={() => {
            this.updateFilters({ categories: '' });
          }}
        />
      );
    }
  }

  renderHasTransparency() {
    const { hasTransparency } = this.props.selectedSearchFilterOptions;

    if (!hasTransparency) {
      return null;
    } else {
      return (
        <AppliedFilter
          label={'Has Transparency'}
          onClear={() => {
            this.updateFilters({ hasTransparency: false });
          }}
        />
      );
    }
  }

  renderResultCount(className = '') {
    const { numberOfResults } = this.props;

    return (
      <ResultCount numberOfResults={numberOfResults} className={className} />
    );
  }

  updateFilters = (properties) => {
    const contentTypeDidChange =
      properties.contentType &&
      properties.contentType !==
        this.props.selectedSearchFilterOptions.contentType;

    const propertiesToChange = {
      page: 1,
      searchOrigin: searchOrigins.APPLIED_FILTERS,
      isPagination: false,
      ...properties,
    };

    if (contentTypeDidChange) {
      propertiesToChange.categories = '';
    }

    const newSearchOptions =
      this.props.selectedSearchFilterOptions.update(propertiesToChange);
    this.props.updateSearchOptionsAndRefresh(newSearchOptions);

    contentTypeDidChange && this.props.setHideSubMenu();
  };
}

function mapStateToProps(state) {
  const pagination = selectPagination(state);

  return {
    selectedSearchFilterOptions: selectSearchFilterOptions(state),
    shouldUpdateSearchFilters: selectShouldUpdateSearchFilters(state),
    numberOfResults: pagination.totalResults,
    isLoading: selectSearchIsLoading(state),
    features: selectSearchFeatures(state),
  };
}

export default connect(mapStateToProps, {
  setHideSubMenu,
  updateSearchOptionsAndRefresh,
})(GraphicAppliedFilters);
